import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useCanonicalRecord, useReverseConsolidation } from '@/api/hooks';
import { failureOf } from '@/api/errors';
import { Refusal } from '@/ui/Refusal';
import { Button, Field, Skeleton, TextInput } from '@/ui/primitives';
import type { ConsolidationEvent } from '@norum/contracts';

const words: Record<string, string> = {
  legalName: 'Legal name',
  displayName: 'Display name',
  domain: 'Domain',
  postalAddress: 'Postal address',
  phone: 'Phone',
  website: 'Website',
  orgNumber: 'Registration number',
  note: 'Notes',
  fullName: 'Name',
  email: 'Email',
};

/** Replace an active source URL with its canonical URL without adding a
 * redirect entry to browser history. Query and history state are identifiers
 * only and remain subject to the destination's normal permission checks. */
export function useCanonicalRedirect(kind: 'company' | 'contact', id: string | null) {
  const canonical = useCanonicalRecord(kind, id ?? '', Boolean(id));
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id || !canonical.data?.canonicalId || canonical.data.canonicalId === id) return;
    void navigate(
      {
        pathname: `/${kind === 'company' ? 'companies' : 'contacts'}/${canonical.data.canonicalId}`,
        search: location.search,
      },
      {
        replace: true,
        preventScrollReset: true,
        state: location.state,
      },
    );
  }, [canonical.data?.canonicalId, id, kind, location.search, location.state, navigate]);
}

/** Values are read live from originals, never copied into a merge snapshot. */
export function CanonicalPanel({ kind, id }: { kind: 'company' | 'contact'; id: string }) {
  const q = useCanonicalRecord(kind, id);
  if (q.isPending) return <Skeleton rows={3} />;
  if (q.isError) return <Refusal failure={failureOf(q.error)} />;
  const record = q.data;
  return (
    <section className="flex flex-col gap-4" aria-label="Identity and history">
      <p>
        Each original keeps its own history, approvals, consent and opt-outs. Consolidation changes
        the company view; it gives no permission to act as another original.
      </p>
      {record.canonicalId !== id && (
        <Link
          className="underline"
          to={`/${kind === 'company' ? 'companies' : 'contacts'}/${record.canonicalId}?tab=identity`}
        >
          Open the primary original
        </Link>
      )}
      <dl className="grid grid-cols-2 gap-2">
        {Object.entries(record.descriptions ?? {}).map(([field, description]) => (
          <div key={`description:${field}`}>
            <dt>{words[field] ?? field}</dt>
            <dd className="m-0 whitespace-pre-wrap">{description.text} · Human {description.mode==='combine'?'combination':'rewording'} of {description.sourceIds.length} originals</dd>
          </div>
        ))}
        {Object.entries(record.fields ?? {}).map(([field, selection]) => (
          <div key={field} className={record.descriptions?.[field]?'text-meta':''}>
            <dt>{words[field] ?? field}</dt>
            <dd className="m-0 whitespace-pre-wrap">
              {selection.available
                ? (selection.value ?? 'Not recorded')
                : 'Selected source is no longer available'}{' '}
              ·{' '}
              <Link
                className="underline"
                to={`/${kind === 'company' ? 'companies' : 'contacts'}/${selection.sourceId}?tab=identity`}
              >
                Source original
              </Link>
            </dd>
          </div>
        ))}
      </dl>
      {(record.alternatives ?? []).map((source) => (
        <article key={source.sourceId} className="border border-line p-4">
          <Link
            className="underline"
            to={`/${kind === 'company' ? 'companies' : 'contacts'}/${source.sourceId}?tab=identity`}
          >
            {source.fields.legalName ?? source.fields.fullName ?? 'Original record'}
            {source.sourceId === record.canonicalId ? ' · Primary original' : ''}
          </Link>
          <dl className="grid grid-cols-2 gap-2">
            {Object.entries(source.fields)
              .filter(([, value]) => value !== null)
              .map(([field, value]) => (
                <div key={field}>
                  <dt>{words[field] ?? field}</dt>
                  <dd className="m-0 whitespace-pre-wrap">{value}</dd>
                </div>
              ))}
          </dl>
          <p>Original updated {new Date(source.at).toLocaleString('en-GB')}</p>
        </article>
      ))}
      {(record.events ?? []).map((event) => (
        <Decision key={event.id} event={event} fields={words} />
      ))}
      {!record.events?.length && <p>No consolidation decisions are recorded for this original.</p>}
    </section>
  );
}
function Decision({
  event,
  fields,
}: {
  event: ConsolidationEvent;
  fields: Record<string, string>;
}) {
  const reverse = useReverseConsolidation();
  const [reason, setReason] = useState('');
  return (
    <article className="border border-line p-4 flex flex-col gap-3">
      <p>
        Consolidated {new Date(event.at).toLocaleString('en-GB')}
        {event.reversedAt
          ? ` · Reversed ${new Date(event.reversedAt).toLocaleString('en-GB')}`
          : ''}
      </p>
      <p className="whitespace-pre-wrap">{event.reason}</p>
      {event.automation&&<p className="text-dense">Automated by a time-bounded human-owned identity policy after reviewed calibrated evidence. The policy and evaluation provenance are retained.</p>}
      {event.reverseReason && (
        <p className="whitespace-pre-wrap">Reversal: {event.reverseReason}</p>
      )}
      {Object.keys(event.choices).length > 0 && (
        <p>
          Explicit field choices:{' '}
          {Object.keys(event.choices)
            .map((field) => fields[field] ?? field)
            .join(', ')}
          . Values remain on the selected originals.
        </p>
      )}
      {Object.keys(event.descriptions ?? {}).length > 0 && (
        <p>Descriptive choices: {Object.entries(event.descriptions).map(([field,value])=>`${fields[field]??field} (${value.mode})`).join(', ')}. The originals remain visible alongside this human-authored text.</p>
      )}
      {!event.reversedAt && (
        <>
          <Field label="Reason for reversal">
            <TextInput
              maxLength={2000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          {reverse.isError && <Refusal failure={failureOf(reverse.error)} />}
          <Button
            disabled={!reason.trim() || reverse.isPending}
            onClick={() => reverse.mutate({ eventId: event.id, reason })}
          >
            Reverse consolidation
          </Button>
        </>
      )}
    </article>
  );
}

export function ContactIdentity({ id }: { id: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <details onToggle={(e) => setExpanded(e.currentTarget.open)}>
      <summary>Identity and history</summary>
      {expanded && <CanonicalPanel kind="contact" id={id} />}
    </details>
  );
}
